// Dashboard de Concursos é acessível a todos os usuários autenticados.
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAchievements } from '@/hooks/useAchievements';

import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/AppLayout';
import { DashboardSkeleton } from '@/components/DashboardSkeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { PerformanceLineChart, DisciplineBarChart } from '@/components/PerformanceChart';
import { LevelProgress, StreakWidget, AchievementsPreview } from '@/components/GamificationWidgets';
import { WeeklyGoals } from '@/components/WeeklyGoals';
import { DailyProgressWidget } from '@/components/DailyProgressWidget';
import { DailyTimeGoalWidget } from '@/components/DailyTimeGoalWidget';
import { GoalStreakWidget } from '@/components/GoalStreakWidget';
import { StreakRiskBanner } from '@/components/StreakRiskBanner';
import { AIAssistant } from '@/components/AIAssistant';

import { useTargetExams } from '@/hooks/useTargetExams';
import { toast } from 'sonner';
import { sanitizeQuestionStatement } from '@/lib/questionUtils';
import { handleSupabaseError } from '@/lib/errors/handleSupabaseError';
import { useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { BookOpen, CheckCircle, XCircle, Target, ArrowRight, Star, Trophy, FileText, Timer, Brain } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Stats { totalAnswered: number; correctAnswers: number; favoriteCount: number; }
interface RecentAnswer { id: string; is_correct: boolean; answered_at: string; question_id: string; question: { statement: string; difficulty: string | null; discipline: { name: string; color: string } | null } | null; }

const getDifficultyBadge = (difficulty: string | null | undefined) => {
  switch (difficulty) {
    case 'facil': return { label: 'Fácil', className: 'bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400' };
    case 'medio': return { label: 'Médio', className: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400' };
    case 'dificil': return { label: 'Difícil', className: 'bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400' };
    default: return null;
  }
};
interface AnswerWithDiscipline { is_correct: boolean; answered_at: string; question: { difficulty: string | null; discipline: { name: string; color: string } | null } | null; }

function TargetExamsToast() {
  const { exams: targetExams, isLoading } = useTargetExams('concursos');
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || !targetExams || targetExams.length === 0) return;
    if (sessionStorage.getItem('target_exams_toast_shown') === '1') return;
    sessionStorage.setItem('target_exams_toast_shown', '1');

    const fmt = (e: { name: string; exam_date: string | null }) => {
      if (!e.exam_date) return e.name;
      const days = Math.ceil((new Date(e.exam_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (days < 0) return `${e.name} (encerrado)`;
      if (days === 0) return `${e.name} (hoje)`;
      return `${e.name} (${days} ${days === 1 ? 'dia' : 'dias'})`;
    };

    const shown = targetExams.slice(0, 3).map(fmt).join(' • ');
    const more = targetExams.length > 3 ? `… +${targetExams.length - 3}` : '';

    toast('Seus concursos alvo', {
      description: shown + more,
      duration: 5000,
      action: {
        label: 'Gerenciar',
        onClick: () => navigate('/concursos/settings'),
      },
    });
  }, [targetExams, isLoading, navigate]);

  return null;
}


function DashboardTopWidgets({ accuracyRate, stats }: { accuracyRate: number; stats: Stats }) {
  const isMobile = useIsMobile();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on('select', () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  const widgets = [
    <LevelProgress key="level" />,
    <StreakWidget key="streak" />,
    <DailyProgressWidget key="daily" />,
    <DailyTimeGoalWidget key="time" />,
    <Card key="accuracy" className="h-[130px] md:min-h-[160px] md:h-full flex flex-col overflow-hidden border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-500/5 to-transparent card-hover-gradient hover-lift">
      <CardHeader className="pb-1 p-3 md:p-6 md:pb-1"><CardTitle className="text-xs md:text-sm font-medium">Taxa de Acerto</CardTitle></CardHeader>
      <CardContent className="flex-1 flex flex-col justify-center pt-0 p-3 md:p-6 md:pt-0">
        <div className="text-lg md:text-2xl font-bold text-primary">{accuracyRate}%</div>
        <Progress value={accuracyRate} className="mt-1 md:mt-2 h-1.5 md:h-2" />
        <p className="text-[10px] md:text-xs text-muted-foreground mt-1">{stats.correctAnswers}/{stats.totalAnswered} corretas</p>
      </CardContent>
    </Card>,
  ];

  if (isMobile) {
    return (
      <div className="space-y-2">
        <Carousel setApi={setApi} opts={{ align: 'start' }} className="w-full">
          <CarouselContent className="-ml-2">
            {widgets.map((widget, i) => (
              <CarouselItem key={i} className="pl-2 basis-[75%]">
                <div className="animate-fade-in" style={{ animationDelay: `${i * 50}ms` }}>
                  {widget}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="flex justify-center gap-1.5">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === current ? 'w-4 bg-primary' : 'w-1.5 bg-muted-foreground/30'
              }`}
              onClick={() => api?.scrollTo(i)}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-2 sm:gap-3 md:gap-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
      {widgets.map((widget, i) => (
        <div key={i} className="animate-fade-in" style={{ animationDelay: `${i * 50}ms` }}>
          {widget}
        </div>
      ))}
    </div>
  );
}


function DashboardInner() {
  const { user } = useAuth();
  const { checkAchievements } = useAchievements();
  const isMobile = useIsMobile();
  const [chartsApi, setChartsApi] = useState<CarouselApi>();
  const [chartsIndex, setChartsIndex] = useState(0);
  const [chartsCount, setChartsCount] = useState(0);
  const [stats, setStats] = useState<Stats>({ totalAnswered: 0, correctAnswers: 0, favoriteCount: 0 });
  const [recentAnswers, setRecentAnswers] = useState<RecentAnswer[]>([]);
  const [allAnswers, setAllAnswers] = useState<AnswerWithDiscipline[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      supabase.from('user_answers').select('id, is_correct, answered_at, question_id, question:questions(statement, difficulty, discipline:disciplines(name, color))').eq('user_id', user.id).eq('module_type', 'concursos').order('answered_at', { ascending: false }).eq('is_official', true),
      supabase.from('favorites').select('id').eq('user_id', user.id),
      supabase.from('user_answers').select(`is_correct, answered_at, question:questions(difficulty, discipline:disciplines(name, color))`).eq('user_id', user.id).eq('module_type', 'concursos').order('answered_at', { ascending: true }).eq('is_official', true),
    ])
      .then(([answersRes, favoritesRes, allAnswersRes]) => {
        if (answersRes.data) {
          setStats({ totalAnswered: answersRes.data.length, correctAnswers: answersRes.data.filter(a => a.is_correct).length, favoriteCount: favoritesRes.data?.length || 0 });
          setRecentAnswers(answersRes.data.slice(0, 5));
        }
        if (allAnswersRes.data) setAllAnswers(allAnswersRes.data as AnswerWithDiscipline[]);
        setLoading(false);

        // Check for new achievements on dashboard load
        checkAchievements();
      })
      .catch((error) => {
        handleSupabaseError(error, 'Não foi possível carregar o painel.', { silent: true, context: { source: 'Dashboard.load' } });
        setLoading(false);
      });
  }, [user, checkAchievements]);

  useEffect(() => {
    if (!chartsApi) return;
    setChartsCount(chartsApi.scrollSnapList().length);
    setChartsIndex(chartsApi.selectedScrollSnap());
    chartsApi.on('select', () => setChartsIndex(chartsApi.selectedScrollSnap()));
  }, [chartsApi]);

  const dailyPerformance = useMemo(() => {
    const byDate: Record<string, { total: number; correct: number }> = {};
    allAnswers.forEach(a => {
      const date = new Date(a.answered_at).toISOString().split('T')[0];
      if (!byDate[date]) byDate[date] = { total: 0, correct: 0 };
      byDate[date].total++;
      if (a.is_correct) byDate[date].correct++;
    });
    return Object.entries(byDate).slice(-14).map(([date, data]) => ({ date, ...data, accuracy: Math.round((data.correct / data.total) * 100) }));
  }, [allAnswers]);

  const disciplinePerformance = useMemo(() => {
    const byDisc: Record<string, { total: number; correct: number; name: string; color: string }> = {};
    allAnswers.forEach(a => {
      const disc = a.question?.discipline;
      if (disc) {
        if (!byDisc[disc.name]) byDisc[disc.name] = { total: 0, correct: 0, name: disc.name, color: disc.color };
        byDisc[disc.name].total++;
        if (a.is_correct) byDisc[disc.name].correct++;
      }
    });
    return Object.values(byDisc).map(d => ({ ...d, accuracy: Math.round((d.correct / d.total) * 100) })).sort((a, b) => b.total - a.total);
  }, [allAnswers]);

  const difficultyPerformance = useMemo(() => {
    const stats = {
      facil: { total: 0, correct: 0, label: 'Fácil', color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
      medio: { total: 0, correct: 0, label: 'Médio', color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
      dificil: { total: 0, correct: 0, label: 'Difícil', color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' }
    };
    allAnswers.forEach(a => {
      const diff = a.question?.difficulty as keyof typeof stats;
      if (diff && stats[diff]) {
        stats[diff].total++;
        if (a.is_correct) stats[diff].correct++;
      }
    });
    return Object.entries(stats).map(([key, data]) => ({
      key,
      ...data,
      accuracy: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0
    }));
  }, [allAnswers]);

  const accuracyRate = stats.totalAnswered > 0 ? Math.round((stats.correctAnswers / stats.totalAnswered) * 100) : 0;

  if (loading) {
    return (
      <AppLayout title="Dashboard">
        <DashboardSkeleton />
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Dashboard">
      <div className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-6">

        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Olá, {user?.user_metadata?.first_name || 'Estudante'}! 👋</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Continue seus estudos de onde parou.</p>
        </div>

        <StreakRiskBanner />

        {/* Linha 1: 5 widgets compactos */}
        <DashboardTopWidgets
          accuracyRate={accuracyRate}
          stats={stats}
        />

        <TargetExamsToast />



        {/* Linha 2: Metas Consecutivas */}
        <div className="animate-fade-in" style={{ animationDelay: '250ms' }}>
          <GoalStreakWidget />
        </div>

        {/* Linha 3: Metas e Conquistas lado a lado */}
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
          <div className="animate-fade-in" style={{ animationDelay: '300ms' }}>
            <WeeklyGoals />
          </div>
          <div className="animate-fade-in" style={{ animationDelay: '350ms' }}>
            <AchievementsPreview />
          </div>
        </div>

        <div className="hidden md:grid gap-2 sm:gap-3 md:gap-4 grid-cols-2 md:grid-cols-4">
          <div className="animate-fade-in" style={{ animationDelay: '300ms' }}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/history" className="block">
                  <Card className="cursor-pointer card-hover-gradient hover-lift group">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium">Questões</CardTitle>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-xl sm:text-2xl font-bold">{stats.totalAnswered}</div>
                    </CardContent>
                  </Card>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Total de questões que você respondeu. Clique para ver seu histórico completo.</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="animate-fade-in" style={{ animationDelay: '350ms' }}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/analytics" className="block">
                  <Card className="cursor-pointer card-hover-gradient hover-lift group">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium">Acertos</CardTitle>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-xl sm:text-2xl font-bold text-green-600">{stats.correctAnswers}</div>
                    </CardContent>
                  </Card>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Número de respostas corretas. Clique para ver análises detalhadas.</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="animate-fade-in" style={{ animationDelay: '400ms' }}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/favorites" className="block">
                  <Card className="cursor-pointer card-hover-gradient hover-lift group">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium">Favoritas</CardTitle>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-xl sm:text-2xl font-bold">{stats.favoriteCount}</div>
                    </CardContent>
                  </Card>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Questões salvas para revisar depois. Clique para acessar seus favoritos.</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="animate-fade-in" style={{ animationDelay: '450ms' }}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/discipline-goals" className="block">
                  <Card className="cursor-pointer card-hover-gradient hover-lift group">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium">Disciplinas</CardTitle>
                      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-xl sm:text-2xl font-bold">{disciplinePerformance.length}</div>
                    </CardContent>
                  </Card>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Disciplinas que você estudou. Clique para gerenciar suas metas.</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {isMobile ? (
          <div>
            <Carousel setApi={setChartsApi} opts={{ align: 'start' }}>
              <CarouselContent className="-ml-2">
                <CarouselItem className="pl-2 basis-[85%]">
                  <Card className="h-full flex flex-col card-hover-gradient">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Desempenho por Dificuldade
                      </CardTitle>
                      <CardDescription>Taxa de acerto por nível</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 space-y-4 pt-0">
                      {difficultyPerformance.every(d => d.total === 0) ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Responda questões para ver suas estatísticas
                        </p>
                      ) : (
                        difficultyPerformance.map((diff) => (
                          <div key={diff.key} className={`p-3 rounded-lg ${diff.bgColor}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className={`font-medium ${diff.color}`}>{diff.label}</span>
                              <span className={`text-lg font-bold ${diff.color}`}>{diff.accuracy}%</span>
                            </div>
                            <Progress value={diff.accuracy} className="h-2" />
                            <p className="text-xs text-muted-foreground mt-1">
                              {diff.correct}/{diff.total} questões corretas
                            </p>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </CarouselItem>
                <CarouselItem className="pl-2 basis-[85%]">
                  <DisciplineBarChart data={disciplinePerformance} />
                </CarouselItem>
                <CarouselItem className="pl-2 basis-[85%]">
                  <PerformanceLineChart data={dailyPerformance} />
                </CarouselItem>
              </CarouselContent>
            </Carousel>
            <div className="flex justify-center gap-1.5 mt-2">
              {Array.from({ length: chartsCount }).map((_, i) => (
                <button
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${i === chartsIndex ? 'w-4 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
                  onClick={() => chartsApi?.scrollTo(i)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-3">
            <div className="animate-fade-in" style={{ animationDelay: '500ms' }}>
              <Card className="h-full flex flex-col card-hover-gradient hover-lift">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Desempenho por Dificuldade
                  </CardTitle>
                  <CardDescription>Taxa de acerto por nível</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 space-y-4 pt-0">
                  {difficultyPerformance.every(d => d.total === 0) ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Responda questões para ver suas estatísticas
                    </p>
                  ) : (
                    difficultyPerformance.map((diff) => (
                      <div key={diff.key} className={`p-3 rounded-lg ${diff.bgColor}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`font-medium ${diff.color}`}>{diff.label}</span>
                          <span className={`text-lg font-bold ${diff.color}`}>{diff.accuracy}%</span>
                        </div>
                        <Progress value={diff.accuracy} className="h-2" />
                        <p className="text-xs text-muted-foreground mt-1">
                          {diff.correct}/{diff.total} questões corretas
                        </p>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '550ms' }}>
              <DisciplineBarChart data={disciplinePerformance} />
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '575ms' }}>
              <PerformanceLineChart data={dailyPerformance} />
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
          <div className="animate-fade-in" style={{ animationDelay: '600ms' }}>
            <Card className="h-full flex flex-col card-hover-gradient hover-lift">
              <CardHeader><CardTitle className="text-base sm:text-lg">Ações Rápidas</CardTitle></CardHeader>
              <CardContent className="flex-1 grid grid-cols-2 gap-3 sm:gap-4">
                <Link to="/questions" className="quick-action-btn flex flex-col items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border bg-card text-foreground hover:bg-muted/50 hover-glow aspect-square min-h-[100px]">
                  <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
                  <span className="text-xs sm:text-sm font-medium text-center">Praticar Questões</span>
                </Link>
                <Link to="/simulados" className="quick-action-btn flex flex-col items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border hover:bg-muted/50 hover-glow aspect-square min-h-[100px]">
                  <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-orange-500" />
                  <span className="text-xs sm:text-sm font-medium text-center">Fazer Simulado</span>
                </Link>
                <Link to="/smart-review" className="quick-action-btn flex flex-col items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border hover:bg-muted/50 hover-glow aspect-square min-h-[100px]">
                  <Brain className="h-6 w-6 sm:h-8 sm:w-8 text-purple-500" />
                  <span className="text-xs sm:text-sm font-medium text-center">Meu Caderno</span>
                </Link>
                <Link to="/pomodoro" className="quick-action-btn flex flex-col items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg border hover:bg-muted/50 hover-glow aspect-square min-h-[100px]">
                  <Timer className="h-6 w-6 sm:h-8 sm:w-8 text-amber-500" />
                  <span className="text-xs sm:text-sm font-medium text-center">Modo Pomodoro</span>
                </Link>
              </CardContent>
            </Card>
          </div>
          <div className="animate-fade-in" style={{ animationDelay: '650ms' }}>
            <Card className="h-full flex flex-col card-hover-gradient hover-lift">
              <CardHeader><CardTitle className="text-base sm:text-lg">Atividade Recente</CardTitle><CardDescription className="text-xs sm:text-sm">Suas últimas respostas</CardDescription></CardHeader>
              <CardContent>
                {recentAnswers.length === 0 ? (
                  <div className="py-6 sm:py-8 text-center"><BookOpen className="mx-auto mb-4 h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground/50" /><p className="text-sm text-muted-foreground">Nenhuma questão respondida ainda.</p><Button asChild variant="link" className="mt-2"><Link to="/questions">Começar agora</Link></Button></div>
                ) : (
                  <div className="space-y-3">{recentAnswers.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 rounded-lg border p-3">
                      {a.is_correct ? <CheckCircle className="h-5 w-5 text-green-500 shrink-0" /> : <XCircle className="h-5 w-5 text-destructive shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium" style={{ color: a.question?.discipline?.color || undefined }}>{a.question?.discipline?.name || 'Questão'}</p>
                          {getDifficultyBadge(a.question?.difficulty) && (
                            <Badge variant="secondary" className={getDifficultyBadge(a.question?.difficulty)!.className}>
                              {getDifficultyBadge(a.question?.difficulty)!.label}
                            </Badge>
                          )}
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-xs text-muted-foreground truncate cursor-help">{sanitizeQuestionStatement(a.question?.statement).slice(0, 50) || 'Enunciado indisponível'}...</p>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm">
                            <p className="text-sm">{sanitizeQuestionStatement(a.question?.statement) || 'Enunciado indisponível'}</p>
                          </TooltipContent>
                        </Tooltip>
                        <p className="text-xs text-muted-foreground">{new Date(a.answered_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <Button variant="ghost" size="sm" asChild className="shrink-0"><Link to={`/questions/${a.question_id}`}>Ver</Link></Button>
                    </div>
                  ))}</div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

      </div>
      <AIAssistant />
    </AppLayout>
  );
}
export default function Dashboard() {
  return <DashboardInner />;
}
